import dayjs from 'dayjs';

export const asArray = value => {
  if (Array.isArray(value)) return value;
  try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
};
export const displayValue = value => value == null || value === '' ? '-' :
  typeof value === 'boolean' ? (value ? 'Yes' : 'No') : typeof value === 'object' ? JSON.stringify(value) : String(value);
export const displayDate = value => value && dayjs(value).isValid() ? dayjs(value).format('MM/DD/YYYY') : '-';
export const partName = part => [part.side, part.name].filter(Boolean).join(' ');

// Adapt the authorized response to the original report template. Never read
// browser caches or fetch current case/client data to fill a period's gaps.
export function reportView(report) {
  const pain = asArray(report.pain_entries).map(entry => ({ ...entry, body_parts_data: asArray(entry.body_parts_data) }));
  const painRows = pain.flatMap(entry => {
    const time = entry.time || (entry.created_at ? dayjs(entry.created_at).format('hh:mm A') : '-');
    const parts = entry.body_parts_data.length ? entry.body_parts_data : [{ name: 'Overall', pain_scale: entry.global_pain_level }];
    return parts.map(part => ({ date: entry.date, time: part.time || time, bodyPart: partName(part),
      intensity: part.pain_scale, comments: part.description || '-', duration: part.duration ?? entry.duration,
      loss_of_sleep: part.loss_of_sleep ?? entry.loss_of_sleep }));
  });
  const questions = asArray(report.intake_questions), answers = asArray(report.intake_answers);
  const intakeRows = [...answers.map(answer => ({ ...answer,
    question_text: answer.question_text || questions.find(question => question.id === answer.intake_question_id)?.question_text || `Question ${answer.intake_question_id}` })),
  ...questions.filter(question => !answers.some(answer => answer.intake_question_id === question.id))
    .map(question => ({ ...question, id: `question-${question.id}`, answer_text: 'Not answered' }))];

  // The original chart has one point per day. Keep every entry in the detailed
  // table and use the highest intensity for each body part on a chart day.
  const days = new Map();
  for (const entry of pain) {
    const date = String(entry.date || '').slice(0, 10);
    if (!dayjs(date).isValid()) continue;
    if (!days.has(date)) days.set(date, new Map());
    const parts = entry.body_parts_data.length ? entry.body_parts_data : [{ side: '', name: 'Overall', pain_scale: entry.global_pain_level }];
    for (const part of parts) {
      const key = partName(part), previous = days.get(date).get(key);
      const next = !previous || Number(part.pain_scale) > Number(previous.pain_scale) ? { ...part } : { ...previous };
      next.loss_of_sleep = !!(previous?.loss_of_sleep || part.loss_of_sleep || entry.loss_of_sleep);
      days.get(date).set(key, next);
    }
  }
  const recordedDates = [...days.keys()].sort();
  const from = report.manifest.from_date || recordedDates[0];
  const to = report.manifest.to_date || recordedDates.at(-1);
  const chartDates = [];
  if (from && to && dayjs(from).isValid() && dayjs(to).isValid()) {
    for (let date = dayjs(from); !date.isAfter(dayjs(to), 'day'); date = date.add(1, 'day')) {
      const key = date.format('YYYY-MM-DD');
      chartDates.push({ date: key, body_parts_data: [...(days.get(key)?.values() || [])] });
    }
  }
  const client = report.client || {}, caseData = report.case || {};
  return { clientAndCaseDetail: { case: { ...caseData, client, agency: report.issuer || {} } },
    clientName: client.name || [client.first_name, client.last_name].filter(Boolean).join(' ') || `Case ${report.manifest.case_id}`,
    painRows, chartDates, allBodyParts: [...new Set(painRows.map(row => row.bodyPart))], intakeRows,
    documents: asArray(report.documents), expenses: asArray(report.expenses), appointments: asArray(report.appointments) };
}
